import { useEffect, useMemo, useState } from 'react';
import { RepeatIcon } from 'lucide-react';
import { useCart, useCartActions } from '../../contexts/StoreContext';
import { useSite } from '../../contexts/SiteContext';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { today } from '../../core/format/dates';
import { boxRange, planBoxes } from '../../core/rules/refillPlan';
import { cartRefill, refillRules } from '../../utils/refill';
import { IntervalChoice } from '../shared/IntervalChoice';

/**
 * Wat er bijgevuld wordt, als één regel: "elke X weken een doos met wat op raakt", met de
 * bandbreedte van de dozen. Alleen ritmes waarbij elke doos het minimum haalt (slim bijvullen,
 * `cartRefill` in utils/refill.ts; de afrekenpagina stuurt hetzelfde ritme mee). Haalt geen enkel
 * ritme het: eerlijk zeggen en iets laten toevoegen.
 */
export function CartRhythm() {
  const {
    lines,
    hasSubscription,
    subscriptionIntervalWeeks
  } = useCart();
  const {
    setSubscriptionInterval
  } = useCartActions();
  const {
    policy
  } = useSite();
  const [editing, setEditing] = useState(false);
  const start = today();
  const refill = useMemo(() => cartRefill(lines, subscriptionIntervalWeeks, policy.subscription, start), [lines, subscriptionIntervalWeeks, policy.subscription, start]);
  const {
    items,
    working,
    weeks
  } = refill;

  // Het gekozen ritme werkt niet (meer) met deze producten: bewaar het voorstel.
  useEffect(() => {
    if (hasSubscription && weeks !== subscriptionIntervalWeeks) setSubscriptionInterval(weeks);
  }, [hasSubscription, weeks, subscriptionIntervalWeeks, setSubscriptionInterval]);
  if (!hasSubscription) return null;
  if (refill.tooSmall) {
    return <div role="status" className="mt-2 bg-sand p-4 text-[13px] leading-relaxed text-ink" data-id="element-6974" data-mp="1sxkchx9yfayz">
        <p className="flex items-start gap-2.5" data-id="element-6975" data-mp="1ovgv6s0ltc5s">
          <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6976" data-mp="1oxa3ebqv84fw" />
          <span data-id="element-6977" data-mp="1jygp4h3r89eo">
            Bijvullen kan vanaf {euro(policy.subscription.minBoxValue)} per doos, en met alleen dit kom je daar niet aan.
            <span className="block text-muted" data-id="element-6978" data-mp="1eac4f5bxzlr8">Zet nog een product op bijvullen (bijvoorbeeld je reiniger), of bestel het nu eenmalig.</span>
          </span>
        </p>
      </div>;
  }
  const range = items.length ? boxRange(planBoxes(items, {
    intervalWeeks: weeks,
    rules: refillRules(policy.subscription),
    earliest: start,
    count: 6
  })) : null;
  return <div className="mt-2 bg-blush p-4 text-[13px] leading-relaxed text-ink" data-id="element-6979" data-mp="1t1ccvacovfnd">
      <p className="flex items-start gap-2.5" data-id="element-6980" data-mp="1xx9t7gnycm05">
        <RepeatIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6981" data-mp="1j9oqvoywiqta" />
        <span className="flex-1" data-id="element-6982" data-mp="1o7q6u1116a0j">
          <span id="cart-rhythm-title" data-id="element-6983" data-mp="1o6owjyru9rjv">Elke {weeks} weken een doos, alleen wat bijna op is</span>
          <span className="block text-muted" data-id="element-6984" data-mp="2bw9hgn7jyyvs">
            {range && (range.min === range.max ? `${euro(range.min)} per doos. ` : `Meestal ${euro(range.min)} tot ${euro(range.max)} per doos. `)}
            Nooit een overschot; pauzeren of stoppen kan altijd.
          </span>
        </span>
        {working.length > 1 && <button type="button" onClick={() => setEditing(e => !e)} aria-expanded={editing} className="shrink-0 text-ink underline underline-offset-4 hover:text-clay" data-id="element-6985" data-mp="8edquj4dory">
            {editing ? 'Klaar' : 'Wijzigen'}
          </button>}
      </p>
      {editing && working.length > 1 && <div className="mt-3 pl-[1.625rem]" data-id="element-6986" data-mp="3ii7w92dfrpdi">
          <IntervalChoice compact intervals={working} value={weeks} onChange={setSubscriptionInterval} labelledBy="cart-rhythm-title" data-id="element-6987" data-mp="3dhskgf8sypxn" />
          <p className="mt-2 text-[12px] text-muted" data-id="element-6988" data-mp="3rid2vaat2jqm">
            Vaker = kleinere dozen, minder vaak = grotere. {policyCopy.subscriptionGift}.
          </p>
        </div>}
    </div>;
}